import { gql } from "@apollo/client";
import { alpha, ButtonBase, Collapse, darken, Paper } from "@mui/material";
import videoPlaceholderImage from "assets/video_placeholder.jpg";
import DateTime from "atoms/DateTime";
import VideoThumbnail from "atoms/VideoThumbnail";
import VideoThumbnailStatusLabel from "atoms/VideoThumbnailStatusLabel";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { COLOR_BLACK, COLOR_BLACK_DARKER, COLOR_BLACK_LIGHTER, COLOR_WHITE, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { VIDEO_PLAYER_RATIO } from "helpers/constants";
import { ONSEN_APP } from "helpers/environment";
import { FONT_FAMILY_GRAPHIK, FONT_FAMILY_KNOCKOUT_JUNIOR } from "helpers/FONT";
import { gaEvent } from "helpers/gtag";
import { SPACING, SPACING_HALF } from "helpers/spacings";
import { ClockOutline, CloudUploadOutline, Download, TimerSand } from "mdi-material-ui";
import React, { useState } from "react";
import { useComponentSize } from "react-use-size";

import { GamePlayerSelectorFragment } from "./GamePlayerSelector";

export const GameOverviewPageHeaderFragment = gql`
  fragment GameOverviewPageHeaderFragment on Game {
    videoDownloadUrl
    videoProcessing
    videoArchived
    gameVideoStatus
    videoAvailableUntil
    playedAt
    videoHlsUrl
    homeScore
    home {
      id
      name
    }
    awayScore
    away {
      id
      name
    }
    league {
      id
      name
    }
    org {
      id
      timeZoneName
    }
    thumbnailUrl
    statsVisible
    ...GamePlayerSelectorFragment
  }
  ${GamePlayerSelectorFragment}
`;

const VIDEO_STATUS_CONFIG = {
  uploading: {
    message: "Video is still uploading, please check back later",
    icon: CloudUploadOutline,
    thumbnail: () => videoPlaceholderImage,
  },
  encoding: {
    message: "Video is still encoding, please check back later",
    icon: TimerSand,
    thumbnail: () => videoPlaceholderImage,
  },
  available: {
    message: null,
    icon: null,
    thumbnail: (game) => game?.thumbnailUrl || videoPlaceholderImage,
  },
};

function getVideoStatusPresentation(game) {
  const status = game?.gameVideoStatus?.toLowerCase();
  const config = status ? VIDEO_STATUS_CONFIG[status] : null;

  if (!config) {
    return { status, label: null, thumbnailUrl: null };
  }

  return {
    status,
    label: config.message ? <VideoThumbnailStatusLabel message={config.message} icon={config.icon} /> : null,
    thumbnailUrl: config.thumbnail(game),
  };
}

export default function GameOverviewPageHeader({ game, tab }) {
  const [downloadHovering, downloadHoveringSet] = useState(false);
  const componentSize = useComponentSize();
  const mobileLayout = componentSize?.width ? componentSize.width <= MAX_CONTENT_WIDTH : false;
  const horizontalPadding = mobileLayout ? 0 : "185px";
  const { status: videoStatus, label: videoStatusLabel, thumbnailUrl } = getVideoStatusPresentation(game);
  const showVideoSection = ["available", "uploading", "encoding"].includes(videoStatus);
  const showPlayButton = videoStatus === "available";

  const homeTeamScoreColor = game?.homeScore >= game?.awayScore ? COLOR_WHITE_LIGHTER : alpha(COLOR_WHITE_LIGHTER, 0.6);
  const awayTeamScoreColor = game?.homeScore <= game?.awayScore ? COLOR_WHITE_LIGHTER : alpha(COLOR_WHITE_LIGHTER, 0.6);
  const colorDarken = darken(COLOR_BLACK_LIGHTER, 0.5);

  return (
    <>
      <div
        ref={componentSize.ref}
        style={{
          background: `linear-gradient(0deg, ${COLOR_BLACK_LIGHTER}, ${colorDarken})`,
          color: COLOR_WHITE,
        }}
      >
        <div
          style={{
            width: `calc(100vw - ${SPACING} * 2)`,
            maxWidth: MAX_CONTENT_WIDTH,
            margin: `${SPACING} auto 0 auto`,
          }}
        >
          <div
            style={{
              padding: SPACING,
              paddingLeft: horizontalPadding,
              paddingRight: horizontalPadding,
              gap: SPACING_HALF,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "min(4vw, 22px)",
              fontFamily: FONT_FAMILY_KNOCKOUT_JUNIOR,
              fontWeight: 400,
            }}
          >
            {!!game?.league?.name && (
              <>
                <div
                  style={{
                    fontSize: "12px",
                    lineHeight: 1,
                    letterSpacing: "1.2px",
                    color: alpha(COLOR_WHITE_LIGHTER, 0.6),
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  {game.league.name}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    lineHeight: 1,
                    color: alpha(COLOR_WHITE_LIGHTER, 0.6),
                    textAlign: "center",
                    fontFamily: FONT_FAMILY_GRAPHIK,
                  }}
                >
                  <DateTime value={game.playedAt} timeZone={game.org.timeZoneName} variant="time_weekday_date" />
                </div>
              </>
            )}
            {game?.statsVisible && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: SPACING_HALF,
                  marginBottom: SPACING_HALF,
                  gap: mobileLayout ? "10px" : SPACING,
                  width: "100%",
                  textTransform: "uppercase",
                }}
              >
                <div
                  style={{
                    flex: "1 1 80em",
                    fontSize: "1.05em",
                    textAlign: "right",
                    paddingRight: mobileLayout ? 0 : SPACING,
                    color: homeTeamScoreColor,
                    ...(mobileLayout && {
                      fontSize: "0.85em",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }),
                  }}
                >
                  {game?.home.name}
                </div>
                <div
                  style={{
                    fontSize: "2.6em",
                    letterSpacing: "1.4px",
                    color: homeTeamScoreColor,
                  }}
                >
                  {game?.homeScore}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, opacity: 0.5 }}>&#58;</div>
                <div
                  style={{
                    fontSize: "2.6em",
                    letterSpacing: "1.4px",
                    color: awayTeamScoreColor,
                  }}
                >
                  {game?.awayScore}
                </div>
                <div
                  style={{
                    flex: "1 1 80em",
                    textAlign: "left",
                    fontSize: "1.05em",
                    paddingLeft: mobileLayout ? 0 : SPACING,
                    color: awayTeamScoreColor,
                    ...(mobileLayout && {
                      fontSize: "0.85em",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }),
                  }}
                >
                  {game?.away.name}
                </div>
              </div>
            )}
          </div>
          <Collapse in={!tab}>
            <div
              style={{
                display: "flex",
                flexFlow: "row nowrap",
                justifyContent: "center",
                alignItems: "stretch",
                padding: `0 ${horizontalPadding}`,
                gap: SPACING,
              }}
            >
              {showVideoSection && (
                <>
                  <ButtonBase
                    disabled={!game?.videoHlsUrl}
                    href={`/game/${game?.id}/video`}
                    style={{
                      flex: "1 1 auto",
                      aspectRatio: VIDEO_PLAYER_RATIO,
                      position: "relative",
                    }}
                  >
                    <VideoThumbnail
                      component={Paper}
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        borderTopLeftRadius: 18,
                        borderTopRightRadius: 18,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        boxShadow: "none",
                      }}
                      processing={game?.videoProcessing}
                      showPlayButton={showPlayButton}
                      videoStatusLabel={videoStatusLabel}
                      thumbnailUrl={thumbnailUrl}
                      leftLabel={
                        showPlayButton && game?.videoAvailableUntil ? (
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "row nowrap",
                              alignItems: "center",
                              gap: "0.2em",
                              fontSize: "0.95em",
                              backgroundColor: alpha(COLOR_BLACK_DARKER, 0.7),
                              padding: "8px 10px",
                              borderRadius: 8,
                            }}
                          >
                            <ClockOutline style={{ fontSize: "1em" }} />
                            Available until {new Date(game.videoAvailableUntil).toLocaleDateString()}
                          </div>
                        ) : undefined
                      }
                      rightLabel={
                        !ONSEN_APP && showPlayButton ? (
                          <ButtonBase
                            component="button"
                            type="button"
                            onMouseEnter={() => downloadHoveringSet(true)}
                            onMouseLeave={() => downloadHoveringSet(false)}
                            disabled={!game?.videoDownloadUrl}
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              if (game?.videoDownloadUrl) {
                                gaEvent("download", { type: "event" });
                                window?.open(game.videoDownloadUrl, "_blank", "noopener,noreferrer");
                              }
                            }}
                            title={"Download Game Video"}
                            style={{
                              borderRadius: 8,
                              padding: "8px 10px",
                              backgroundColor: downloadHovering ? COLOR_BLACK : alpha(COLOR_BLACK_DARKER, 0.7),
                              fontFamily: FONT_FAMILY_GRAPHIK,
                              fontWeight: 400,
                              fontSize: "0.95em",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Download style={{ fontSize: "1em", marginRight: 4 }} />
                            <span>Download Game Video</span>
                          </ButtonBase>
                        ) : undefined
                      }
                    />
                  </ButtonBase>
                </>
              )}
            </div>
          </Collapse>
        </div>
      </div>
    </>
  );
}
